import { useState, useEffect } from 'react'
import { Users, UserPlus, Shield, Clock } from 'lucide-react'

interface UsersData {
  total: number
  active: number
  admins: number
  managers: number
  users: number
  lastLogin: string
}

export default function UsersCard() {
  const [usersData, setUsersData] = useState<UsersData>({
    total: 0,
    active: 0,
    admins: 0,
    managers: 0,
    users: 0,
    lastLogin: new Date().toISOString()
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchUsersData()
  }, [])

  const fetchUsersData = async () => {
    try {
      setLoading(true)
      const response = await fetch('/api/users-pg')
      const data = await response.json()

      if (data.success) {
        const users = data.data
        const admins = users.filter((u: any) => u.role === 'admin').length
        const managers = users.filter((u: any) => u.role === 'manager').length
        const regularUsers = users.filter((u: any) => u.role === 'user').length
        const activeUsers = users.filter((u: any) => u.active === true).length

        // Get most recent login
        const sortedByLogin = [...users].sort((a: any, b: any) => {
          if (!a.last_login) return 1
          if (!b.last_login) return -1
          return new Date(b.last_login).getTime() - new Date(a.last_login).getTime()
        })
        const lastLogin = sortedByLogin[0]?.last_login || new Date().toISOString()

        setUsersData({
          total: users.length,
          active: activeUsers,
          admins,
          managers,
          users: regularUsers,
          lastLogin
        })
      }
    } catch (error) {
      console.error('Error fetching users:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{
      background: 'linear-gradient(135deg, #1A1F2E, #242938)',
      border: '2px solid #FFD93D',
      borderRadius: '12px',
      padding: '24px',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: '0 0 20px rgba(255, 217, 61, 0.2)',
      height: '100%'
    }}>
      {/* Corner accent */}
      <div style={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: '100px',
        height: '100px',
        background: 'linear-gradient(135deg, transparent 50%, rgba(255, 217, 61, 0.2) 50%)',
        pointerEvents: 'none'
      }} />

      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '20px'
      }}>
        <div style={{
          background: 'rgba(255, 217, 61, 0.2)',
          border: '2px solid #FFD93D',
          borderRadius: '8px',
          padding: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Users style={{ width: 24, height: 24, color: '#FFD93D' }} />
        </div>
        <h3 style={{
          fontFamily: 'Rajdhani, sans-serif',
          fontSize: '20px',
          fontWeight: 700,
          color: '#FFD93D',
          textTransform: 'uppercase',
          letterSpacing: '1px',
          margin: 0,
          textShadow: '0 0 10px rgba(255, 217, 61, 0.6)'
        }}>
          User Management
        </h3>
      </div>

      {/* Total Users */}
      <div style={{
        background: 'rgba(255, 217, 61, 0.1)',
        border: '1px solid rgba(255, 217, 61, 0.3)',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div>
          <div style={{
            fontSize: '11px',
            color: '#8A95AA',
            letterSpacing: '1px',
            textTransform: 'uppercase',
            fontWeight: 600,
            marginBottom: '4px'
          }}>
            Total Users
          </div>
          <div style={{
            fontSize: '32px',
            fontWeight: 700,
            color: '#FFD93D',
            fontFamily: 'Rajdhani, sans-serif'
          }}>
            {usersData.total}
          </div>
        </div>
        <div style={{
          fontSize: '14px',
          color: '#00FF00',
          fontWeight: 700,
          fontFamily: 'Rajdhani, sans-serif'
        }}>
          {usersData.active} Active
        </div>
      </div>

      {/* Role Breakdown */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: '12px',
        marginBottom: '16px'
      }}>
        <div style={{
          background: 'rgba(255, 107, 53, 0.1)',
          border: '1px solid rgba(255, 107, 53, 0.3)',
          borderRadius: '6px',
          padding: '12px',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '11px',
            color: '#8A95AA',
            letterSpacing: '1px',
            textTransform: 'uppercase',
            fontWeight: 600,
            marginBottom: '4px'
          }}>
            Admins
          </div>
          <div style={{
            fontSize: '20px',
            fontWeight: 700,
            color: '#FF6B35',
            fontFamily: 'Rajdhani, sans-serif'
          }}>
            {usersData.admins}
          </div>
        </div>

        <div style={{
          background: 'rgba(0, 240, 255, 0.1)',
          border: '1px solid rgba(0, 240, 255, 0.3)',
          borderRadius: '6px',
          padding: '12px',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '11px',
            color: '#8A95AA',
            letterSpacing: '1px',
            textTransform: 'uppercase',
            fontWeight: 600,
            marginBottom: '4px'
          }}>
            Managers
          </div>
          <div style={{
            fontSize: '20px',
            fontWeight: 700,
            color: '#00F0FF',
            fontFamily: 'Rajdhani, sans-serif'
          }}>
            {usersData.managers}
          </div>
        </div>

        <div style={{
          background: 'rgba(107, 207, 127, 0.1)',
          border: '1px solid rgba(107, 207, 127, 0.3)',
          borderRadius: '6px',
          padding: '12px',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '11px',
            color: '#8A95AA',
            letterSpacing: '1px',
            textTransform: 'uppercase',
            fontWeight: 600,
            marginBottom: '4px'
          }}>
            Users
          </div>
          <div style={{
            fontSize: '20px',
            fontWeight: 700,
            color: '#6BCF7F',
            fontFamily: 'Rajdhani, sans-serif'
          }}>
            {usersData.users}
          </div>
        </div>
      </div>

      {/* Action Button */}
      <button style={{
        width: '100%',
        padding: '12px',
        background: 'linear-gradient(135deg, #FFD93D, #FFC300)',
        border: '2px solid #FFD93D',
        borderRadius: '6px',
        color: '#1A1F2E',
        cursor: 'pointer',
        fontWeight: 700,
        fontSize: '14px',
        textTransform: 'uppercase',
        fontFamily: 'Rajdhani, sans-serif',
        boxShadow: '0 0 20px rgba(255, 217, 61, 0.4)',
        transition: 'all 0.2s',
        marginBottom: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 0 30px rgba(255, 217, 61, 0.6)'
        e.currentTarget.style.transform = 'translateY(-2px)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 0 20px rgba(255, 217, 61, 0.4)'
        e.currentTarget.style.transform = 'translateY(0)'
      }}
      onClick={() => window.location.href = '/admin/users'}
      >
        <Shield style={{ width: 16, height: 16 }} />
        Manage Users
      </button>

      {/* Last Login */}
      <div style={{
        fontSize: '11px',
        color: '#8A95AA',
        textAlign: 'center'
      }}>
        Last Login: {new Date(usersData.lastLogin).toLocaleString()}
      </div>
    </div>
  )
}
